import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";
import AgenteForm from "./AgenteForm";

const API_URL = process.env.REACT_APP_API_URL || "";

const roleBadge = (role) => {
  const r = (role || "").toUpperCase();
  if (r === "MADRE") return "bg-dark";
  if (r === "AGENTE") return "bg-primary";
  if (r === "FRANCHISING") return "bg-warning text-dark";
  if (r === "AZIENDA") return "bg-info text-dark";
  if (r === "PV") return "bg-success";
  return "bg-secondary";
};

const Avatar = ({ text }) => {
  const initials = (text || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center me-2"
      style={{
        width: 40,
        height: 40,
        background:
          "linear-gradient(135deg, rgba(13,110,253,.15), rgba(111,66,193,.15))",
        border: "1px solid rgba(0,0,0,.05)",
        fontWeight: 700,
      }}
    >
      {initials}
    </div>
  );
};

export default function AgentiPage() {
  const [agenti, setAgenti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [storeFilter, setStoreFilter] = useState("");
  const [ordina, setOrdina] = useState("username-asc");

  const nav = useNavigate();

  useEffect(() => {
    fetchAgenti();
  }, []);

  const fetchAgenti = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agenti/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
      });
      const payload = await res.json();
      const arr = Array.isArray(payload) ? payload : payload.agenti || [];
      setAgenti(arr);
    } catch (err) {
      console.error(err);
      alert("Errore caricamento agenti");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    fetchAgenti();
    setShowForm(false);
  };

  const counters = useMemo(() => {
    const byRole = agenti.reduce((acc, a) => {
      const r = (a.role || "ND").toUpperCase();
      acc[r] = (acc[r] || 0) + 1;
      return acc;
    }, {});
    return { total: agenti.length, byRole };
  }, [agenti]);

  const agentiView = useMemo(() => {
    let arr = [...agenti];

    const k = q.trim().toLowerCase();
    if (k) {
      arr = arr.filter((a) =>
        [a.username, a.email, a.role, a.store_name]
          .map((v) => String(v || "").toLowerCase())
          .some((v) => v.includes(k))
      );
    }

    if (roleFilter !== "ALL") {
      arr = arr.filter(
        (a) => (a.role || "").toUpperCase() === roleFilter.toUpperCase()
      );
    }

    const s = storeFilter.trim().toLowerCase();
    if (s) {
      arr = arr.filter((a) =>
        String(a.store_name || "").toLowerCase().includes(s)
      );
    }

    if (ordina === "username-asc")
      arr.sort((a, b) => (a.username || "").localeCompare(b.username || ""));
    if (ordina === "username-desc")
      arr.sort((a, b) => (b.username || "").localeCompare(a.username || ""));
    if (ordina === "role-asc")
      arr.sort((a, b) => (a.role || "").localeCompare(b.role || ""));
    if (ordina === "role-desc")
      arr.sort((a, b) => (b.role || "").localeCompare(a.role || ""));

    return arr;
  }, [agenti, q, roleFilter, storeFilter, ordina]);

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="m-0">🧑‍💼 Agenti</h2>
        <div className="d-flex gap-2">
          {!showForm ? (
            <button className="btn btn-success" onClick={() => setShowForm(true)}>
              ➕ Aggiungi Agente
            </button>
          ) : (
            <button
              className="btn btn-outline-secondary"
              onClick={() => setShowForm(false)}
            >
              ← Annulla
            </button>
          )}
          <button className="btn btn-outline-primary" onClick={fetchAgenti}>
            🔄 Aggiorna
          </button>
        </div>
      </div>

      {!showForm && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="badge bg-secondary">Totali: {counters.total}</span>
          {Object.entries(counters.byRole).map(([r, n]) => (
            <span key={r} className={`badge ${roleBadge(r)}`}>
              {r}: {n}
            </span>
          ))}
        </div>
      )}

      {!showForm && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label mb-1">Cerca</label>
                <input
                  className="form-control"
                  placeholder="🔎 username, email, ruolo o store…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label mb-1">Ruolo</label>
                <select
                  className="form-select"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="ALL">Tutti</option>
                  <option value="MADRE">SEDE</option>
                  <option value="AGENTE">AGENTE</option>
                  <option value="FRANCHISING">FRANCHISING</option>
                  <option value="AZIENDA">AZIENDA</option>
                  <option value="PV">PV</option>
                </select>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label mb-1">Store</label>
                <input
                  className="form-control"
                  placeholder="Es. ZioRoby"
                  value={storeFilter}
                  onChange={(e) => setStoreFilter(e.target.value)}
                />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label mb-1">Ordina</label>
                <select
                  className="form-select"
                  value={ordina}
                  onChange={(e) => setOrdina(e.target.value)}
                >
                  <option value="username-asc">Username (A → Z)</option>
                  <option value="username-desc">Username (Z → A)</option>
                  <option value="role-asc">Ruolo (A → Z)</option>
                  <option value="role-desc">Ruolo (Z → A)</option>
                </select>
              </div>

              <div className="col-12 col-md-12 text-end">
                <button
                  className="btn btn-outline-secondary mt-2"
                  onClick={() => {
                    setQ("");
                    setRoleFilter("ALL");
                    setStoreFilter("");
                    setOrdina("username-asc");
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && <AgenteForm onSuccess={handleSuccess} />}

      {!showForm && (
        <>
          {loading ? (
            <p className="text-muted">⏳ Caricamento…</p>
          ) : agentiView.length === 0 ? (
            <div className="alert alert-info">
              Nessun agente trovato con i filtri attuali.
            </div>
          ) : (
            <div className="row g-3">
              {agentiView.map((agente) => (
                <div className="col-12 col-md-6 col-lg-4" key={agente.id}>
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <Avatar text={agente.username} />
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <h5 className="card-title mb-1 text-truncate">
                              {agente.username}
                            </h5>
                            {agente.role && (
                              <span className={`badge ${roleBadge(agente.role)}`}>
                                {agente.role}
                              </span>
                            )}
                          </div>
                          {agente.email && (
                            <div className="text-muted small">📧 {agente.email}</div>
                          )}
                          {agente.store_name && (
                            <div className="text-muted small">🏬 {agente.store_name}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="card-footer bg-white border-0 d-flex gap-2 pt-0">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => nav(`/agenti/${agente.id}`)}
                      >
                        Apri scheda →
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => nav(`/agenti/${agente.id}/edit`)}
                      >
                        Modifica
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => nav(`/dashboard-agente?user_id=${agente.id}`)}
                        title="Apri dashboard e provvigioni dell'agente"
                      >
                        Dashboard
                      </button>

                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
